/**
 * ProfileAdminView - UI Profile Administration Interface
 *
 * Super admin interface for managing UI profiles stored in the database.
 * Profiles control which modules and widgets are visible to users.
 * Includes the visual Sidebar Builder (replaces separate SidebarCustomizerView).
 */

import React, { useState, useEffect } from 'react';
import {
  Users, UserCheck, LayoutList, LayoutGrid, Settings,
  Plus, RefreshCw, AlertCircle, CheckCircle, RotateCcw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { uiProfileAPI, userAPI, widgetRegistryAPI, helpAPI } from '../../../api';
import { useProfile } from '../../context/ProfileContext';
import { ProfileCard } from './ProfileCard';
import { ProfileFormModal } from './ProfileFormModal';
import { ProfileAssignmentTab } from './ProfileAssignmentTab';
import { SidebarBuilderTab } from './SidebarBuilderTab';
import { ALL_WIDGETS } from '../../constants/profile_constants.js';

// Tab definitions — 4 tabs: Profils, Constructeur Sidebar, Widgets, Affectations
// Labels resolved at render time (see TABS usage below) — this array only carries keys/icons.
const TAB_DEFS = [
  { key: 'profiles', labelKey: 'uiProfiles.admin.tabs.profiles', icon: Users },
  { key: 'sidebar-builder', labelKey: 'uiProfiles.admin.tabs.sidebarBuilder', icon: LayoutList },
  { key: 'widgets', labelKey: 'uiProfiles.admin.tabs.widgets', icon: LayoutGrid },
  { key: 'assignments', labelKey: 'uiProfiles.admin.tabs.assignments', icon: UserCheck },
];

// Stats card component
function StatCard({ icon: Icon, label, value, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-500 dark:text-blue-400 border-blue-500/20',
    green: 'bg-green-500/10 text-green-500 dark:text-green-400 border-green-500/20',
    purple: 'bg-purple-500/10 text-purple-500 dark:text-purple-400 border-purple-500/20',
    amber: 'bg-amber-500/10 text-amber-500 dark:text-amber-400 border-amber-500/20',
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]}`}>
      <div className="flex items-center gap-3">
        <Icon size={20} />
        <div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm opacity-80">{label}</div>
        </div>
      </div>
    </div>
  );
}

// Category colors — labels resolved at render via t('uiProfiles.categories.<code>') (piège #1 : pas de libellé figé ici)
const CATEGORY_COLORS = { operational: 'bg-amber-500/10 border-amber-500/20 text-amber-600', fleet: 'bg-blue-500/10 border-blue-500/20 text-blue-600', monitoring: 'bg-purple-500/10 border-purple-500/20 text-purple-600', financial: 'bg-green-500/10 border-green-500/20 text-green-600', general: 'bg-gray-500/10 border-gray-500/20 text-gray-600' };
const STATUS_BADGE_COLORS = { active: 'bg-green-100 text-green-700', inactive: 'bg-gray-100 text-gray-500', deprecated: 'bg-red-100 text-red-600' };

// Widgets overview tab content - DB-backed with fallback
function WidgetsOverview({ profiles }) {
  const { t } = useTranslation();
  const [registryWidgets, setRegistryWidgets] = useState(null);

  useEffect(() => {
    widgetRegistryAPI.getAll()
      .then(res => {
        if (res?.ok && Array.isArray(res.data) && res.data.length > 0) {
          setRegistryWidgets(res.data);
        }
      })
      .catch(() => {});
  }, []);

  // Merge: DB takes priority, fallback to hardcoded
  const widgets = registryWidgets || ALL_WIDGETS.map((w, i) => ({
    code: w.id, label: w.label, description: '', category: 'general', icon: 'LayoutGrid',
    component_name: '', status: 'active', display_order: i,
  }));

  const categories = [...new Set(widgets.map(w => w.category || 'general'))];

  // Count how many profiles use each widget
  const usageCounts = {};
  (profiles || []).forEach(p => {
    const pw = Array.isArray(p.widgets) ? p.widgets : [];
    pw.forEach(wCode => { usageCounts[wCode] = (usageCounts[wCode] || 0) + 1; });
  });

  return (
    <div className="space-y-6">
      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={LayoutGrid} label={t('uiProfiles.widgets.total')} value={widgets.length} color="blue" />
        <StatCard icon={CheckCircle} label={t('uiProfiles.admin.stats.active')} value={widgets.filter(w => w.status === 'active').length} color="green" />
        <StatCard icon={Settings} label={t('uiProfiles.widgets.categoriesCount')} value={categories.length} color="purple" />
        <StatCard icon={Users} label={t('uiProfiles.widgets.source')} value={registryWidgets ? t('uiProfiles.widgets.sourceDb') : t('uiProfiles.widgets.sourceConstants')} color="amber" />
      </div>

      {/* Widgets by category */}
      {categories.map(cat => {
        const catWidgets = widgets.filter(w => (w.category || 'general') === cat);
        if (catWidgets.length === 0) return null;
        const catColor = CATEGORY_COLORS[cat] || CATEGORY_COLORS.general;
        return (
          <div key={cat} className="bg-white dark:bg-slate-800/50 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${catColor}`}>
                {t(`uiProfiles.categories.${cat}`, { defaultValue: cat })}
              </span>
              <span className="text-sm text-gray-400">{t('uiProfiles.widgets.count', { count: catWidgets.length })}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {catWidgets.map(widget => {
                const code = widget.code || widget.id;
                const usage = usageCounts[code] || 0;
                return (
                  <div
                    key={code}
                    className="p-4 bg-gray-50 dark:bg-slate-700/30 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-500/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{widget.label}</div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${STATUS_BADGE_COLORS[widget.status] || STATUS_BADGE_COLORS.active}`}>
                        {t(`uiProfiles.widgets.status.${widget.status}`, { defaultValue: widget.status })}
                      </span>
                    </div>
                    {widget.description && (
                      <p className="text-xs text-gray-500 dark:text-slate-400 mb-2 line-clamp-2">{widget.description}</p>
                    )}
                    <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-slate-500">
                      <span className="font-mono">{code}</span>
                      <span>{t('uiProfiles.widgets.usage', { count: usage })}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ProfileAdminView({ initialTab }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(initialTab || 'profiles');
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [onboardingResetStatus, setOnboardingResetStatus] = useState(null); // null | 'loading' | 'success' | 'error'

  // Get refreshProfiles from context to update app-wide profile list
  const { refreshProfiles } = useProfile();

  // Load data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [profilesRes, usersRes] = await Promise.all([
        uiProfileAPI.getAll(),
        userAPI.getAll(),
      ]);

      if (profilesRes.ok) {
        // Parse JSON fields
        const parsedProfiles = profilesRes.data.map(p => ({
          ...p,
          modules: typeof p.modules === 'string' ? JSON.parse(p.modules) : p.modules,
          widgets: typeof p.widgets === 'string' ? JSON.parse(p.widgets) : p.widgets,
        }));
        setProfiles(parsedProfiles);
      }

      if (usersRes.ok) {
        setUsers(usersRes.data);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(t('uiProfiles.admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handlers
  const handleCreateProfile = () => {
    setEditingProfile(null);
    setShowFormModal(true);
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setShowFormModal(true);
  };

  const handleDuplicateProfile = async (profile) => {
    const newCode = `${profile.code}_copy`;
    const newName = `${profile.name} (copie)`;
    try {
      await uiProfileAPI.duplicate(profile.id, newCode, newName);
      loadData();
      await refreshProfiles();
    } catch (err) {
      console.error('Failed to duplicate profile:', err);
    }
  };

  const handleDeleteProfile = async (profile) => {
    if (profile.is_system) {
      alert(t('uiProfiles.admin.deleteSystemProfile'));
      return;
    }

    // Check if profile has assigned users
    const usersWithProfile = users.filter(u => u.ui_profile_id === profile.id);
    if (usersWithProfile.length > 0) {
      alert(t('uiProfiles.admin.deleteHasUsers', { count: usersWithProfile.length }));
      return;
    }

    if (!window.confirm(t('uiProfiles.admin.confirmDelete', { name: profile.name }))) return;

    try {
      await uiProfileAPI.delete(profile.id);
      loadData();
      await refreshProfiles();
    } catch (err) {
      console.error('Failed to delete profile:', err);
    }
  };

  const handleSetDefault = async (profile) => {
    try {
      await uiProfileAPI.setDefault(profile.id);
      loadData();
      await refreshProfiles();
    } catch (err) {
      console.error('Failed to set default:', err);
    }
  };

  const handleResetOnboarding = async () => {
    if (!window.confirm(t('uiProfiles.admin.resetOnboarding.confirm'))) return;
    setOnboardingResetStatus('loading');
    const res = await helpAPI.resetOnboarding();
    if (res.ok) {
      setOnboardingResetStatus('success');
      setTimeout(() => setOnboardingResetStatus(null), 3000);
    } else {
      setOnboardingResetStatus('error');
      setTimeout(() => setOnboardingResetStatus(null), 3000);
    }
  };

  const handleFormSave = async (profileData) => {
    try {
      if (editingProfile) {
        await uiProfileAPI.update(editingProfile.id, profileData);
      } else {
        await uiProfileAPI.create(profileData);
      }
      setShowFormModal(false);
      loadData();
      // Refresh app-wide profile context so new profiles appear in selectors immediately
      await refreshProfiles();
    } catch (err) {
      console.error('Failed to save profile:', err);
      throw err;
    }
  };

  // Calculate stats
  const stats = {
    total: profiles.length,
    active: profiles.filter(p => p.status === 'active').length,
    system: profiles.filter(p => p.is_system).length,
    usersWithProfile: users.filter(u => u.ui_profile_id).length,
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profiles':
        return (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label={t('uiProfiles.admin.stats.totalProfiles')} value={stats.total} color="blue" />
              <StatCard icon={CheckCircle} label={t('uiProfiles.admin.stats.active')} value={stats.active} color="green" />
              <StatCard icon={Settings} label={t('uiProfiles.admin.stats.system')} value={stats.system} color="purple" />
              <StatCard icon={UserCheck} label={t('uiProfiles.admin.stats.usersAssigned')} value={stats.usersWithProfile} color="amber" />
            </div>

            {/* Profile cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map(profile => (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  userCount={users.filter(u => u.ui_profile_id === profile.id).length}
                  onEdit={() => handleEditProfile(profile)}
                  onDuplicate={() => handleDuplicateProfile(profile)}
                  onDelete={() => handleDeleteProfile(profile)}
                  onSetDefault={() => handleSetDefault(profile)}
                />
              ))}

              {/* Add new profile card */}
              <button
                onClick={handleCreateProfile}
                className="p-6 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600 hover:border-blue-500 hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors flex flex-col items-center justify-center gap-2 min-h-[200px]"
              >
                <Plus size={32} className="text-gray-400 dark:text-slate-400" />
                <span className="text-gray-400 dark:text-slate-400 font-medium">{t('uiProfiles.admin.newProfile')}</span>
              </button>
            </div>
          </div>
        );

      case 'sidebar-builder':
        return <SidebarBuilderTab profiles={profiles} />;

      case 'widgets':
        return <WidgetsOverview profiles={profiles} />;

      case 'assignments':
        return (
          <ProfileAssignmentTab
            profiles={profiles}
            users={users}
            onRefresh={loadData}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('uiProfiles.admin.title')}</h1>
          <p className="text-gray-500 dark:text-slate-400 mt-1">
            {t('uiProfiles.admin.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleResetOnboarding}
            disabled={onboardingResetStatus === 'loading'}
            className={`px-3 py-2 rounded-lg border flex items-center gap-2 text-sm transition-colors shadow-sm ${
              onboardingResetStatus === 'success'
                ? 'bg-green-50 border-green-300 text-green-700'
                : onboardingResetStatus === 'error'
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700'
            }`}
            title={t('uiProfiles.admin.resetOnboarding.title')}
          >
            <RotateCcw size={16} className={onboardingResetStatus === 'loading' ? 'animate-spin' : ''} />
            {onboardingResetStatus === 'success' ? t('uiProfiles.admin.resetOnboarding.success') : onboardingResetStatus === 'error' ? t('common.messages.errorTitle') : t('uiProfiles.admin.resetOnboarding.button')}
          </button>
          <button
            onClick={loadData}
            className="p-2 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors shadow-sm"
          >
            <RefreshCw size={20} />
          </button>
          <button
            onClick={handleCreateProfile}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            {t('uiProfiles.admin.newProfile')}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-500 dark:text-red-400">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-slate-800/50 rounded-xl w-fit">
        {TAB_DEFS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700'
              }`}
            >
              <Icon size={18} />
              {t(tab.labelKey)}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {renderTabContent()}

      {/* Form Modal */}
      {showFormModal && (
        <ProfileFormModal
          profile={editingProfile}
          onSave={handleFormSave}
          onClose={() => setShowFormModal(false)}
        />
      )}
    </div>
  );
}

export default ProfileAdminView;
